import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, type FormikProps } from 'formik';
import { Text, TextArea } from '@rsos/base-components';
import { AgencyStates } from '@rsos/capstone/src/constants/Systems';
import { Button } from '@rsos/components/capstone/base';
import type { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { TOW_PRO } from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import {
  closeResourcePanel,
  markTransportSeen,
  setIsEditing,
  setResourcePanelView,
  setSelectedRequest,
  useBreadCrumbs,
  useCreateTowProRequestMutation,
  useGetTowProMunicipalitiesQuery,
  useUpdateTowProRequestMutation,
  type TowProRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { panToGlobalMap } from '@rsos/utils/panToGlobalMap';
import { Label } from '../../../../AirAmbulanceForm/AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import SelectLocationBanner from '../../../../AirAmbulanceForm/AirAmbulanceForm/SelectLocationBanner/SelectLocationBanner';
import TowProAddressField from './TowProAddressField';
import styles from './TowProForm.module.css';
import TowProFormSection from './TowProFormSection';
import { MultiSelectField, SelectField, TextField } from './shared';
import fieldStyles from './shared/FormField.module.css';
import { callerIdMatchesInput, getCallLocation } from './towProCallLookup';
import {
  type TowProFormValues,
  type TowProSelectOption,
} from './towProFormConstants';
import {
  buildTowProDispatchRequest,
  findTowProReason,
  formValuesFromRequest,
  getVinWarning,
  normalizeVin,
  validateTowProForm,
} from './towProFormUtils';
import { useTowProInitialFormValues } from './useTowProInitialFormValues';
import {
  type TowProLocationSource,
  useTowProLocationField,
} from './useTowProLocationField';
import { useTowProVinAutofill } from './useTowProVinAutofill';

const STATE_OPTIONS: TowProSelectOption[] = AgencyStates.map(
  ({ label }: { label: string }) => ({ value: label, label }),
);

/**
 * One option per call in the queue, which is keyed by caller ID. A request
 * being edited can reference a call that has since left the queue; that call
 * stays listed so the field still shows it.
 */
function buildRelatedCallOptions(
  callerIds: string[],
  currentCallerId: string,
): TowProSelectOption[] {
  const options =
    currentCallerId && !callerIds.includes(currentCallerId)
      ? [currentCallerId, ...callerIds]
      : callerIds;
  return options.map(callerId => ({
    value: callerId,
    label: formatInternationalPhoneNumberHuman(callerId),
  }));
}

const filterRelatedCallOption = (
  option: TowProSelectOption,
  input: string,
): boolean => callerIdMatchesInput(option.value, input);

const SUBMIT_ERROR_MESSAGE =
  'Unable to submit the tow request. Please try again.';

interface TowProFormProps {
  /** When set the form edits this request and resubmits it to TowPro. */
  existingRequest?: TowProRequestResponse | null;
}

/**
 * Tow request form for creating a request or editing an active one. Section
 * layout mirrors the TowPro design: Request (location, region, reason,
 * related call), Vehicle, Incident & Officer, Vehicle Owner, and Additional
 * notes.
 */
function TowProForm({ existingRequest = null }: TowProFormProps): JSX.Element {
  const dispatch = useDispatch();
  const breadCrumbs = useBreadCrumbs();
  const formRef = useRef<FormikProps<TowProFormValues>>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [createTowProRequest, { isLoading: isCreating }] =
    useCreateTowProRequestMutation();
  const [updateTowProRequest, { isLoading: isUpdating }] =
    useUpdateTowProRequestMutation();
  const isSubmitting = isCreating || isUpdating;
  const { data: municipalities, isFetching: isLoadingMunicipalities } =
    useGetTowProMunicipalitiesQuery();

  const regionOptions = useMemo((): TowProSelectOption[] => {
    if (!municipalities) {
      return [];
    }
    return municipalities.subMunicipalities.flatMap(subMunicipality =>
      subMunicipality.regions.map(region => ({
        value: String(region.id),
        label: region.name,
      })),
    );
  }, [municipalities]);

  // Every sub-municipality carries its own agency list, and the same agency
  // can serve more than one, so duplicates are dropped by ID.
  const agencyOptions = useMemo((): TowProSelectOption[] => {
    if (!municipalities) {
      return [];
    }
    const agencies = municipalities.subMunicipalities.flatMap(
      subMunicipality => subMunicipality.agencies,
    );
    return [...new Map(agencies.map(agency => [agency.id, agency])).values()]
      .map(agency => ({ value: String(agency.id), label: agency.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [municipalities]);

  // Reasons are configured per municipality in TowPro, so the options and
  // their sub-reasons both come from the municipalities lookup.
  const reasons = useMemo(
    () => municipalities?.reasons ?? [],
    [municipalities],
  );
  const reasonOptions = useMemo(
    (): TowProSelectOption[] =>
      reasons.map(reason => ({
        value: String(reason.id),
        label: reason.name,
      })),
    [reasons],
  );

  // The location follows whichever changed last: the related call or a pick
  // on the map. A map pick resolves its address asynchronously, so once a
  // call's location (or a typed address) replaces it, the stale address must
  // not land on top of the newer location.
  const acceptsReverseGeocode = useRef(true);

  const handleLocationResolved = useCallback(
    (
      location: {
        address?: string;
        latitude?: number;
        longitude?: number;
      },
      source: TowProLocationSource,
    ): void => {
      const formik = formRef.current;
      if (!formik) {
        return;
      }
      if (source === 'reverse-geocode') {
        if (!acceptsReverseGeocode.current) {
          return;
        }
      } else {
        acceptsReverseGeocode.current = source === 'map';
      }
      if ('address' in location) {
        formik.setFieldValue('address', location.address ?? '');
      }
      if ('latitude' in location) {
        formik.setFieldValue('latitude', location.latitude ?? null);
      }
      if ('longitude' in location) {
        formik.setFieldValue('longitude', location.longitude ?? null);
      }
    },
    [],
  );

  const {
    geocodedResults,
    isReverseGeocoding,
    handleAddressChange,
    handleSelectSuggestion,
    triggerReverseGeocode,
  } = useTowProLocationField({
    onLocationResolved: handleLocationResolved,
    existingLocation: existingRequest?.request.data.addressInput.location,
  });

  // The call only seeds the form when it opens. Feeding later call changes
  // through `initialValues` would reinitialize the form and wipe every other
  // edit, so they are merged into the call-derived fields below instead.
  const callSeededValues = useTowProInitialFormValues();
  const [openingCallValues] = useState(callSeededValues);
  const initialValues = useMemo(
    () =>
      existingRequest
        ? formValuesFromRequest(existingRequest)
        : openingCallValues,
    [existingRequest, openingCallValues],
  );
  const lastCallSeededValues = useRef(callSeededValues);
  const callQueue = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.callQueue,
  );
  const queuedCallerIds = useMemo(
    () => Object.keys(callQueue ?? {}),
    [callQueue],
  );
  const handleVinChange = useTowProVinAutofill(formRef);

  // A call location can arrive as bare coordinates; resolve its address once
  // on open.
  useEffect(() => {
    if (
      !initialValues.address &&
      initialValues.latitude != null &&
      initialValues.longitude != null
    ) {
      triggerReverseGeocode({
        latitude: initialValues.latitude,
        longitude: initialValues.longitude,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mount
  }, []);

  // Switching calls replaces the related call, and the location when the call
  // has one, with the new call's, leaving the rest of the form as the user
  // left it.
  useEffect(() => {
    if (lastCallSeededValues.current === callSeededValues) {
      return;
    }
    lastCallSeededValues.current = callSeededValues;

    const formik = formRef.current;
    if (existingRequest || !formik) {
      return;
    }

    // Same rule as picking a related call: a call without a location leaves
    // the current location in place.
    const { related_call, address, latitude, longitude } = callSeededValues;
    if (latitude == null || longitude == null) {
      formik.setFieldValue('related_call', related_call, true);
      return;
    }

    formik.setValues(
      { ...formik.values, related_call, address, latitude, longitude },
      true,
    );
    acceptsReverseGeocode.current = !address;
    if (!address) {
      triggerReverseGeocode({ latitude, longitude });
    }
  }, [callSeededValues, existingRequest, triggerReverseGeocode]);

  // Picking a call replaces the location with the call's, even one picked on
  // the map, and pans the map to it. A typed number, or a call without a
  // location, leaves the current location and the map in place.
  const handleRelatedCallChange = useCallback(
    (value: string): void => {
      const formik = formRef.current;
      if (!formik) {
        return;
      }

      const callLocation = getCallLocation(callQueue?.[value]);
      if (!callLocation) {
        formik.setFieldValue('related_call', value, true);
        return;
      }

      formik.setValues(
        {
          ...formik.values,
          related_call: value,
          address: callLocation.address,
          latitude: callLocation.latitude,
          longitude: callLocation.longitude,
        },
        true,
      );
      panToGlobalMap(callLocation.latitude, callLocation.longitude);
      acceptsReverseGeocode.current = !callLocation.address;
      if (!callLocation.address) {
        triggerReverseGeocode({
          latitude: callLocation.latitude,
          longitude: callLocation.longitude,
        });
      }
    },
    [callQueue, triggerReverseGeocode],
  );

  const handleCancel = (): void => {
    if (existingRequest) {
      dispatch(setIsEditing(false));
      return;
    }
    if (breadCrumbs.includes('list_requests') && breadCrumbs.length > 1) {
      dispatch(setResourcePanelView('list_requests'));
    } else {
      dispatch(closeResourcePanel());
    }
  };

  // A reason's sub-reasons only exist on the fetched reason, so validation
  // needs them to know whether Secondary Reason is required.
  const validate = useCallback(
    (values: TowProFormValues) => validateTowProForm(values, reasons),
    [reasons],
  );

  const hasBlockingErrors = (values: TowProFormValues): boolean =>
    Object.keys(validate(values)).length > 0;

  // Sub-reasons belong to one reason, so a selection made under the previous
  // reason would be rejected by TowPro. Both fields are written in a single
  // update: setting them one at a time validates the cleared sub-reasons
  // against the reason on its way out, which leaves the new reason wrongly
  // holding the outgoing one's "required" error.
  const handleReasonChange = useCallback((value: string): void => {
    const formik = formRef.current;
    if (!formik) {
      return;
    }
    formik.setValues(
      { ...formik.values, tow_reason_id: value, tow_subreason_ids: [] },
      true,
    );
  }, []);

  const handleSubmit = async (values: TowProFormValues): Promise<void> => {
    const body = buildTowProDispatchRequest(values);
    if (!body) {
      return;
    }

    setSubmitError(null);
    try {
      if (existingRequest) {
        await updateTowProRequest({
          requestId: existingRequest.id,
          body,
        }).unwrap();
        dispatch(setIsEditing(false));
        return;
      }

      const created = await createTowProRequest(body).unwrap();
      dispatch(markTransportSeen(String(created.id)));
      dispatch(
        setSelectedRequest({
          requestId: String(created.id),
          detailView: 'towpro',
        }),
      );
    } catch (error) {
      // etl_integrations forwards TowPro's own message as `detail`; it is
      // more actionable than the generic copy when we have it.
      setSubmitError(extractErrorMessage(error, SUBMIT_ERROR_MESSAGE));
    }
  };

  return (
    <div className={styles.form} data-name="towpro-form">
      <div className={styles.title}>
        <Text color="primaryText" size="large" fontWeight="bold">
          {existingRequest ? 'Edit Tow Request' : 'New Tow Request'}
        </Text>
      </div>

      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        validate={validate}
        validateOnMount
        validateOnChange={false}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {formik => {
          const subReasons =
            findTowProReason(reasons, formik.values.tow_reason_id)
              ?.subReasons ?? [];
          const subReasonOptions: TowProSelectOption[] = subReasons.map(
            subReason => ({
              value: String(subReason.id),
              label: subReason.name,
            }),
          );
          const relatedCallOptions = buildRelatedCallOptions(
            queuedCallerIds,
            formik.values.related_call,
          );

          return (
            <Form id="towProForm">
              <TowProFormSection title="Request" defaultExpanded={true}>
                <SelectField
                  formik={formik}
                  name="related_call"
                  label="Related Call"
                  options={relatedCallOptions}
                  placeholder="Search calls or enter a number"
                  isClearable
                  isSearchable
                  filterOption={filterRelatedCallOption}
                  allowTypedValue
                  onChange={handleRelatedCallChange}
                />
                <TowProAddressField
                  value={formik.values.address}
                  error={
                    formik.touched.address && formik.errors.address
                      ? formik.errors.address
                      : undefined
                  }
                  geocodedResults={geocodedResults}
                  isReverseGeocoding={isReverseGeocoding}
                  onAddressChange={handleAddressChange}
                  onSelectSuggestion={handleSelectSuggestion}
                  onBlur={formik.handleBlur}
                />
                <SelectLocationBanner geoSelectionKey={TOW_PRO} />
                <SelectField
                  formik={formik}
                  name="region_id"
                  label="Region*"
                  options={regionOptions}
                  placeholder="Select region"
                  isLoading={isLoadingMunicipalities}
                />
                <SelectField
                  formik={formik}
                  name="tow_reason_id"
                  label="Reason*"
                  options={reasonOptions}
                  placeholder="Select reason"
                  isLoading={isLoadingMunicipalities}
                  onChange={handleReasonChange}
                />
                <MultiSelectField
                  formik={formik}
                  name="tow_subreason_ids"
                  label={
                    subReasonOptions.length > 0
                      ? 'Secondary Reason*'
                      : 'Secondary Reason'
                  }
                  options={subReasonOptions}
                  placeholder={
                    subReasonOptions.length > 0
                      ? 'Select secondary reason'
                      : 'No secondary reasons'
                  }
                  isDisabled={subReasonOptions.length === 0}
                />
              </TowProFormSection>

              <TowProFormSection title="Vehicle" defaultExpanded={false}>
                <TextField
                  formik={formik}
                  name="vin"
                  label="VIN"
                  placeholder="Enter VIN"
                  onValueChange={handleVinChange}
                  transformValue={normalizeVin}
                  warning={
                    formik.touched.vin
                      ? (getVinWarning(formik.values.vin) ?? undefined)
                      : undefined
                  }
                />
                <div className={styles.row}>
                  <TextField formik={formik} name="make" label="Make" />
                  <TextField formik={formik} name="model" label="Model" />
                </div>
                <div className={styles.row}>
                  <TextField formik={formik} name="vehicle_year" label="Year" />
                  <TextField formik={formik} name="color" label="Color" />
                </div>
                <div className={styles.row}>
                  <TextField
                    formik={formik}
                    name="license_plate_number"
                    label="License plate #"
                  />
                  <SelectField
                    formik={formik}
                    name="license_plate_state"
                    label="License plate state"
                    options={STATE_OPTIONS}
                    placeholder="State"
                  />
                </div>
              </TowProFormSection>

              <TowProFormSection
                title="Incident & Officer"
                defaultExpanded={false}
              >
                <TextField
                  formik={formik}
                  name="incident_number"
                  label="Incident number"
                />
                <SelectField
                  formik={formik}
                  name="agency_id"
                  label="Agency"
                  options={agencyOptions}
                  placeholder="Select agency"
                  isLoading={isLoadingMunicipalities}
                  isClearable
                />
                <div className={styles.row}>
                  <TextField
                    formik={formik}
                    name="officer_name"
                    label="Officer name"
                    placeholder="Full name"
                  />
                  <TextField
                    formik={formik}
                    name="badge_number"
                    label="Badge #"
                  />
                </div>
              </TowProFormSection>

              <TowProFormSection title="Vehicle Owner" defaultExpanded={false}>
                <div className={styles.row}>
                  <TextField
                    formik={formik}
                    name="owner_first_name"
                    label="First name"
                  />
                  <TextField
                    formik={formik}
                    name="owner_last_name"
                    label="Last name"
                  />
                </div>
                <TextField
                  formik={formik}
                  name="owner_phone"
                  label="Phone"
                  placeholder="(555) 555-5555"
                />
                <TextField
                  formik={formik}
                  name="owner_street"
                  label="Street"
                  placeholder="Street address"
                />
                <div className={styles.row}>
                  <TextField formik={formik} name="owner_apt" label="Apt" />
                  <TextField formik={formik} name="owner_city" label="City" />
                </div>
                <div className={styles.row}>
                  <SelectField
                    formik={formik}
                    name="owner_state"
                    label="State"
                    options={STATE_OPTIONS}
                    placeholder="State"
                  />
                  <TextField
                    formik={formik}
                    name="owner_zip"
                    label="Zip code"
                  />
                </div>
              </TowProFormSection>

              <TowProFormSection title="Additional" defaultExpanded={false}>
                <div className={fieldStyles.field}>
                  <Label>
                    <Text size="small" color="primaryText">
                      Notes
                    </Text>
                  </Label>
                  <TextArea
                    id="notes"
                    name="notes"
                    placeholder="Optional context"
                    rows={3}
                    fontSize="14px"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </TowProFormSection>

              {submitError && (
                <div className={styles.errorBanner} role="alert">
                  <Text color="background" size="small">
                    {submitError}
                  </Text>
                </div>
              )}
              <div className={styles.buttons}>
                <Button
                  grow
                  btnType="tertiary"
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  mx={0}
                  dataName="towpro-form-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  grow
                  type="submit"
                  btnType="primary"
                  disabled={isSubmitting || hasBlockingErrors(formik.values)}
                  dataName="towpro-form-submit-button"
                >
                  {isSubmitting
                    ? 'Submitting...'
                    : existingRequest
                      ? 'Resend / Update'
                      : 'Submit'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default TowProForm;
